import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "../lib/api";

const getSessionId = () => {
  let id = localStorage.getItem("vincen_chat_session");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("vincen_chat_session", id);
  }
  return id;
};

const WELCOME = {
  role: "assistant",
  content: "Step inside the house of Vincen. I'm Aurelia, your personal concierge — here to help you explore the collections, find your perfect fit, and order with ease. How may I assist you today?",
};

const SUGGESTIONS = [
  "Do I need an account to order?",
  "Help me find my size",
  "Tell me about Pebble The Otter",
  "What makes Vincen special?",
];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const sendText = async (raw) => {
    const text = (raw || "").trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Forgive me — I'm momentarily unavailable. Please try again, or email vinceninfo@gmail.com." }]);
    } finally {
      setLoading(false);
    }
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    sendText(text);
  };

  const lastMsg = messages[messages.length - 1];
  const showSuggestions = lastMsg && lastMsg.role === "assistant" && !loading;

  const renderRich = (text) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
      p.startsWith("**") && p.endsWith("**") ? (
        <strong key={i} className="text-gold font-medium">{p.slice(2, -2)}</strong>
      ) : (
        p
      )
    );

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open concierge chat"
        className="fixed bottom-6 right-6 z-[120] w-14 h-14 flex items-center justify-center bg-gold text-black shadow-[0_8px_30px_rgba(212,175,55,0.35)] hover:bg-gold-hover transition-colors duration-500 rounded-full"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X size={22} strokeWidth={1.75} />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle size={22} strokeWidth={1.75} />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[120] w-[calc(100vw-3rem)] sm:w-[380px] h-[520px] max-h-[75vh] flex flex-col bg-[#0C0C0C] border border-gold/25 shadow-[0_24px_80px_rgba(0,0,0,0.7)]"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <span className="lux-star w-1.5 h-1.5 bg-gold rotate-45" />
              <div>
                <p className="font-serif text-lg text-[#F5F5F5] leading-none">Aurelia</p>
                <p className="text-[9px] tracking-[0.3em] uppercase text-gold mt-1">Vincen Concierge</p>
              </div>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[90%] whitespace-pre-line ${
                      m.role === "user"
                        ? "bg-maroon text-[#F5F5F5] px-4 py-2.5 text-sm leading-relaxed rounded-md"
                        : "bg-[#161616] text-zinc-200 border border-white/5 px-5 py-4 text-[13.5px] leading-7 tracking-[0.01em] rounded-md"
                    }`}
                  >
                    {m.role === "assistant" ? renderRich(m.content) : m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start" data-testid="chat-typing">
                  <div className="bg-[#161616] border border-white/5 px-4 py-3 flex gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="w-1.5 h-1.5 bg-gold/70 rounded-full animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}

              {showSuggestions && (
                <div className="pt-1 flex flex-wrap gap-2" data-testid="chat-suggestions">
                  {SUGGESTIONS.map((q) => (
                    <button
                      key={q}
                      data-testid={`chat-suggestion-${q.slice(0, 12).replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                      onClick={() => sendText(q)}
                      className="text-left text-[11px] tracking-[0.04em] text-zinc-300 border border-gold/30 px-3 py-2 hover:border-gold hover:text-gold hover:bg-gold/5 transition-colors duration-300"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask the concierge..."
                className="flex-1 bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-[#F5F5F5] placeholder:text-zinc-600 focus:border-gold focus:outline-none transition-colors duration-300"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={loading || !input.trim()}
                aria-label="Send"
                className="w-11 h-11 flex items-center justify-center bg-gold text-black hover:bg-gold-hover transition-colors duration-300 disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
